import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Input, Select, TextArea } from "../../components/Form";
import useButtonLoader from "../../hooks/UseButtonLoader";
import { toastMessage } from "../../utils/toastMessage";
import {
  getSelectedConsultantFormApi,
  submitSelectedConsultantFormApi,
} from "../../services/consultantManagement";

export const initialSelectedConsultantForm = {
  email: "",
  name: "",
  userName: "",
  dateOfBirth: "",
  gender: "",
  profileBio: "",
  primaryNumber: "",
  secondaryNumber: "",
  highestEducationalQualification: "",
  currentProfession: "",
  primarySkill: "",
  otherSkill: "",
  learningSource: "",
  experienceYears: "",
  dailyWorkHours: "",
  expertise: "",
  preferLanguage: "",
  topics: [],
  address: "",
  city: "",
  state: "",
  pincode: "",
  heardAboutVedicMeet: "",
  otherOnlinePlatformWork: "",
  otherOnlinePlatformName: "",
  favouriteSong: "",
  zodiacSign: "",
  movieGenre: "",
  hobbies: "",
  placeOfBirth: "",
  highestQualification: "",
  minimumEarningExpectation: "",
  astrologyLearningSource: "",
  referredToVedicMeet: "",
  fullTimeJob: "",
  panCardNumber: "",
  aadharCardNumber: "",
  accountNumber: "",
  ifscCode: "",
  bankName: "",
  bankBranch: "",
};

export const selectedConsultantFileFields = [
  { key: "profileImage", label: "High Quality Profile Picture" },
  { key: "galleryPhotos", label: "Gallery Photos", multiple: true },
  { key: "videoIntroduction", label: "Video Introduction", optional: true },
  { key: "aadharFront", label: "Aadhar Card Front Side" },
  { key: "aadharBack", label: "Aadhar Card Back Side" },
  { key: "panCardFront", label: "Pan Card Front Side" },
  { key: "marksheet10th", label: "10th Marksheet" },
  { key: "certificateOfCompletion", label: "Certificate of Completion" },
  { key: "bankProof", label: "Cheque / Pass Book / Bank Statement" },
];

const consultantTopicOptions = [
  { name: "Relationships & Marriage", details: "Toxic relationships, Heartbreak, Divorce, Second marriage" },
  { name: "Affairs, Trust & Emotional Pain", details: "Extra-marital affairs, Loyalty doubts, Still attached to ex" },
  { name: "Mental & Emotional Well-Being", details: "Overthinking, Depression feelings" },
  { name: "Spiritual Journey", details: "Spiritual talks" },
  { name: "Intimacy & Personal Matters", details: "Sex life issues" },
  { name: "Family & Home Affairs", details: "Family disputes, In-laws problems, Joint family conflicts, Property disputes" },
  { name: "Private & Sensitive Matters", details: "Secret relationships, Hidden relationships, Things you can't share with family, Things you can't tell friends, Guilt & regret" },
];

const normalizeSelectedTopics = (value) => {
  if (Array.isArray(value)) {
    return [...new Set(value.map((topic) => typeof topic === "object" ? topic.name : topic).filter(Boolean))];
  }
  if (!value) return [];
  try {
    return normalizeSelectedTopics(JSON.parse(value));
  } catch (_) {
    return String(value).split(",").map((topic) => topic.trim()).filter(Boolean);
  }
};

const correctionFieldMap = {
  profile: ["email", "name", "userName", "dateOfBirth", "gender", "profileBio", "primaryNumber", "secondaryNumber"],
  professional: ["highestEducationalQualification", "currentProfession", "primarySkill", "otherSkill", "learningSource", "experienceYears", "dailyWorkHours", "expertise", "preferLanguage", "topics"],
  address: ["address", "city", "state", "pincode", "placeOfBirth"],
  preferences: ["heardAboutVedicMeet", "otherOnlinePlatformWork", "otherOnlinePlatformName", "favouriteSong", "zodiacSign", "movieGenre", "hobbies"],
  additional: ["highestQualification", "minimumEarningExpectation", "astrologyLearningSource", "referredToVedicMeet", "fullTimeJob"],
  pan: ["panCardNumber"],
  bank: ["name", "accountNumber", "ifscCode", "bankName", "bankBranch"],
  documents: ["aadharCardNumber"],
};

const correctionFileMap = {
  photos: ["profileImage", "galleryPhotos"],
  video: ["videoIntroduction"],
  pan: ["panCardFront"],
  bank: ["bankProof"],
  documents: ["aadharFront", "aadharBack", "marksheet10th", "certificateOfCompletion"],
};

export const SelectedConsultantFormContent = ({
  token,
  embedded = false,
  shouldFetchDetails = true,
  submitApi = submitSelectedConsultantFormApi,
  onSubmitted,
  onCancel,
  submitLabel = "Submit",
}) => {
  const [form, setForm] = useState(initialSelectedConsultantForm);
  const [files, setFiles] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [correctionMode, setCorrectionMode] = useState(false);
  const [editableSections, setEditableSections] = useState([]);
  const [correctionRemark, setCorrectionRemark] = useState("");
  const [buttonref, buttonLoader] = useButtonLoader(submitLabel);

  useEffect(() => {
    if (!shouldFetchDetails || !token) return;
    const fetchFormDetails = async () => {
      try {
        const { data } = await getSelectedConsultantFormApi(token);
        if (data.code === 200) {
          const result = data.result || {};
          const correctionData = result.correctionMode ? result.formData || {} : {};
          setForm((prev) => ({
            ...prev,
            ...correctionData,
            name: correctionData.name || result.name || "",
            email: correctionData.email || result.email || "",
            minimumEarningExpectation: correctionData.minimumEarningExpectation || result.price || "",
            topics: normalizeSelectedTopics(correctionData.topics),
          }));
          setSubmitted(Boolean(result.submitted));
          setCorrectionMode(Boolean(result.correctionMode));
          setEditableSections(result.editableSections || []);
          setCorrectionRemark(result.correctionRemark || "");
        } else {
          toastMessage(data.message, "error", "selectedForm");
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchFormDetails();
  }, [shouldFetchDetails, token]);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "otherOnlinePlatformWork" && value !== "Yes" ? { otherOnlinePlatformName: "" } : {}),
    }));
  };

  const handleFile = (key, fileList, multiple = false) => {
    setFiles((prev) => ({ ...prev, [key]: multiple ? Array.from(fileList) : fileList?.[0] }));
  };

  const handleTopicChange = (topicName) => {
    setForm((prev) => ({
      ...prev,
      topics: prev.topics.includes(topicName)
        ? prev.topics.filter((topic) => topic !== topicName)
        : [...prev.topics, topicName],
    }));
  };

  const isFieldEditableForCorrection = (key) =>
    editableSections.some((section) => (correctionFieldMap[section] || []).includes(key));

  const isFileEditableForCorrection = (key) =>
    editableSections.some((section) => (correctionFileMap[section] || []).includes(key));

  const isFieldDisabled = (key) => correctionMode && !isFieldEditableForCorrection(key);
  const isFileDisabled = (key) => correctionMode && !isFileEditableForCorrection(key);

  const submitHandler = async () => {
    try {
      buttonLoader(true);
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => formData.append(key, Array.isArray(value) ? JSON.stringify(value) : value || ""));
      Object.entries(files).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((file) => formData.append(key, file));
        } else if (value) {
          formData.append(key, value);
        }
      });

      const { data } = await submitApi(token, formData);
      if (data.code === 200) {
        setSubmitted(true);
        toastMessage(data.message, "success", "selectedForm");
        onSubmitted?.(data.result);
      } else {
        toastMessage(data.message, "error", "selectedForm");
      }
    } catch (error) {
      console.log(error);
      toastMessage(
        error?.response?.data?.message || error?.message || "Something went wrong",
        "error",
        "selectedForm"
      );
    } finally {
      buttonLoader(false);
    }
  };

  if (submitted) {
    if (embedded) {
      return (
        <div style={{ padding: "12px 0" }}>
          <h5>Consultant Form Submitted</h5>
          <p>Your details have been submitted successfully. The Vedic Meet team will review them shortly.</p>
        </div>
      );
    }
    return (
      <div className="WrapperArea" style={{ marginLeft: 0 }}>
        <div className="WrapperBox">
          <h4 className="Title">Consultant Form Submitted</h4>
          <p>Your details have been submitted successfully. The Vedic Meet team will review them shortly.</p>
        </div>
      </div>
    );
  }

  const formContent = (
    <>
        <div className="TitleBox">
          <h4 className="Title">
            {correctionMode ? "Vedic Meet Consultant Correction Form" : "Vedic Meet Consultant Onboarding Form"}
          </h4>
        </div>
        {correctionMode && (
          <div className="alert alert-warning" style={{ borderRadius: 6 }}>
            Please update only the rejected section. Other details are locked.
            {correctionRemark ? <div style={{ marginTop: 6 }}><strong>Remark:</strong> {correctionRemark}</div> : null}
          </div>
        )}
        <div className="row">
          <div className="form-group col-md-6 mb-2">
            <Input label="Email" required type="email" disabled={isFieldDisabled("email")} className="form-control" value={form.email} onChange={(e) => handleChange("email", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Name" required type="text" disabled={isFieldDisabled("name")} className="form-control" value={form.name} onChange={(e) => handleChange("name", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Username" required type="text" disabled={isFieldDisabled("userName")} className="form-control" value={form.userName} onChange={(e) => handleChange("userName", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Date of Birth" required type="date" disabled={isFieldDisabled("dateOfBirth")} className="form-control" value={form.dateOfBirth} onChange={(e) => handleChange("dateOfBirth", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Select label="Gender" required disabled={isFieldDisabled("gender")} className="form-control" value={form.gender} onChange={(e) => handleChange("gender", e.target.value)}>
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </Select>
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Primary Number" required type="text" disabled={isFieldDisabled("primaryNumber")} className="form-control" value={form.primaryNumber} onChange={(e) => handleChange("primaryNumber", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Secondary Number" type="text" disabled={isFieldDisabled("secondaryNumber")} className="form-control" value={form.secondaryNumber} onChange={(e) => handleChange("secondaryNumber", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Highest Educational Qualification" required type="text" disabled={isFieldDisabled("highestEducationalQualification")} className="form-control" value={form.highestEducationalQualification} onChange={(e) => handleChange("highestEducationalQualification", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Current Profession" required type="text" disabled={isFieldDisabled("currentProfession")} className="form-control" value={form.currentProfession} onChange={(e) => handleChange("currentProfession", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Primary Skill" required type="text" disabled={isFieldDisabled("primarySkill")} className="form-control" placeholder="Astrology, Palmistry, Numerology, Tarot" value={form.primarySkill} onChange={(e) => handleChange("primarySkill", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Other Skill" type="text" disabled={isFieldDisabled("otherSkill")} className="form-control" placeholder="Card reading, Vedic, Astrology..." value={form.otherSkill} onChange={(e) => handleChange("otherSkill", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Learning Source" required type="text" disabled={isFieldDisabled("learningSource")} className="form-control" value={form.learningSource} onChange={(e) => handleChange("learningSource", e.target.value)} />
          </div>
          <div className="form-group col-md-3 mb-2">
            <Input label="Experience in Years" required type="number" disabled={isFieldDisabled("experienceYears")} className="form-control" value={form.experienceYears} onChange={(e) => handleChange("experienceYears", e.target.value)} />
          </div>
          <div className="form-group col-md-3 mb-2">
            <Input label="Daily Work Hours" required type="number" disabled={isFieldDisabled("dailyWorkHours")} className="form-control" value={form.dailyWorkHours} onChange={(e) => handleChange("dailyWorkHours", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Expertise" required type="text" disabled={isFieldDisabled("expertise")} className="form-control" placeholder="Family, Career, Finance..." value={form.expertise} onChange={(e) => handleChange("expertise", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Input label="Prefer Language" required type="text" disabled={isFieldDisabled("preferLanguage")} className="form-control" placeholder="English, Hindi, Sanskrit..." value={form.preferLanguage} onChange={(e) => handleChange("preferLanguage", e.target.value)} />
          </div>
          <div className="form-group col-md-12 mb-2">
            <label className="my-1 text-dark">
              Which topics are you comfortable talking about?<span className="text-danger"> *</span>
            </label>
            <div className="row mt-1">
              {consultantTopicOptions.map((topic) => (
                <label className="col-md-6 mb-2 d-flex align-items-start" key={topic.name}>
                  <input
                    type="checkbox"
                    className="mt-1 me-2"
                    disabled={isFieldDisabled("topics")}
                    checked={form.topics.includes(topic.name)}
                    onChange={() => handleTopicChange(topic.name)}
                  />
                  <span>
                    <strong>{topic.name}</strong>
                    <small className="d-block text-muted">({topic.details})</small>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="form-group col-md-12 mb-2">
            <TextArea label="Profile Bio" required disabled={isFieldDisabled("profileBio")} className="form-control" value={form.profileBio} onChange={(e) => handleChange("profileBio", e.target.value)} />
          </div>
          {["address", "city", "state", "pincode"].map((key) => (
            <div className="form-group col-md-6 mb-2" key={key}>
              <Input label={key.replace(/([A-Z])/g, " $1")} required type="text" disabled={isFieldDisabled(key)} className="form-control" value={form[key]} onChange={(e) => handleChange(key, e.target.value)} />
            </div>
          ))}
          <div className="form-group col-md-6 mb-2">
            <Input label="Where did you hear about Vedic Meet?" required type="text" disabled={isFieldDisabled("heardAboutVedicMeet")} className="form-control" value={form.heardAboutVedicMeet} onChange={(e) => handleChange("heardAboutVedicMeet", e.target.value)} />
          </div>
          <div className="form-group col-md-6 mb-2">
            <Select label="Work on other online astrologer platform?" required disabled={isFieldDisabled("otherOnlinePlatformWork")} className="form-control" value={form.otherOnlinePlatformWork} onChange={(e) => handleChange("otherOnlinePlatformWork", e.target.value)}>
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </Select>
          </div>
          {form.otherOnlinePlatformWork === "Yes" && (
            <div className="form-group col-md-6 mb-2">
              <Input label="Which app/platform have you worked on?" required type="text" disabled={isFieldDisabled("otherOnlinePlatformName")} className="form-control" value={form.otherOnlinePlatformName} onChange={(e) => handleChange("otherOnlinePlatformName", e.target.value)} />
            </div>
          )}
          {["favouriteSong", "zodiacSign", "movieGenre", "hobbies", "placeOfBirth", "highestQualification", "minimumEarningExpectation", "astrologyLearningSource", "referredToVedicMeet", "fullTimeJob", "panCardNumber", "aadharCardNumber", "accountNumber", "ifscCode", "bankName", "bankBranch"].map((key) => (
            <div className="form-group col-md-6 mb-2" key={key}>
              <Input label={key.replace(/([A-Z])/g, " $1")} required={!["astrologyLearningSource"].includes(key)} type="text" disabled={isFieldDisabled(key)} className="form-control" value={form[key]} onChange={(e) => handleChange(key, e.target.value)} />
            </div>
          ))}
          {selectedConsultantFileFields.map((field) => (
            <div className="form-group col-md-6 mb-2" key={field.key}>
              <label className="my-1 text-dark">
                {field.label}
                {!isFileDisabled(field.key) && !field.optional && <span className="text-danger"> *</span>}
                {!isFileDisabled(field.key) && field.optional && <span className="text-secondary"> (optional)</span>}
              </label>
              <input type="file" disabled={isFileDisabled(field.key)} className="form-control" multiple={field.multiple} onChange={(e) => handleFile(field.key, e.target.files, field.multiple)} />
            </div>
          ))}
        </div>
        <div className="model_custom_footer mt-3">
          {onCancel && (
            <button type="button" className="btn_size" style={{ background: "#6c757d" }} onClick={onCancel}>
              Cancel
            </button>
          )}
          <button className="btn_size" ref={buttonref} onClick={submitHandler}>{submitLabel}</button>
        </div>
    </>
  );

  if (embedded) return formContent;

  return (
    <div className="WrapperArea" style={{ marginLeft: 0 }}>
      <div className="WrapperBox">
        {formContent}
      </div>
    </div>
  );
};

const SelectedConsultantForm = () => {
  const { token } = useParams();
  return <SelectedConsultantFormContent token={token} />;
};

export default SelectedConsultantForm;
